/**
 * Hooks for entity data: rows, relationships, type filters.
 */

import { useMemo } from "react";
import type {
  EntityRow,
  EntityRole,
  EntityRelationships,
  EntitiesDataTableProps,
} from "../types";
import { GROUP_CATEGORY_META } from "../constants";

const CATEGORY_PRIORITY = [
  "officers",
  "shareholders",
  "related_companies",
  "subsidiaries",
  "trusts",
  "secured_parties",
  "professionals",
  "other",
];

/**
 * Merge person rows that share the same name and parent entity into a
 * single combined row with multiple roles. Non-person entities are
 * returned as-is.
 */
function mergePersonRows(rows: EntityRow[]): EntityRow[] {
  const personGroups = new Map<string, EntityRow[]>();
  const result: EntityRow[] = [];

  for (const row of rows) {
    if (row.type !== "person") {
      result.push(row);
      continue;
    }
    const key = `${row.name.toLowerCase().trim()}::${row.relatedTo ?? ""}`;
    const group = personGroups.get(key);
    if (group) {
      group.push(row);
    } else {
      personGroups.set(key, [row]);
    }
  }

  for (const group of personGroups.values()) {
    if (group.length === 1) {
      result.push(group[0]);
      continue;
    }

    group.sort(
      (a, b) =>
        CATEGORY_PRIORITY.indexOf(a.groupCategory) -
        CATEGORY_PRIORITY.indexOf(b.groupCategory),
    );

    const primary = group[0];
    const roles: EntityRole[] = group.map((r) => ({
      group: r.group,
      groupCategory: r.groupCategory,
      groupEntityRef: r.groupEntityRef,
      groupEntityName: r.groupEntityName,
      detail: r.detail,
    }));

    const seenDocIds = new Set<string>();
    const mergedDocs: Array<{ id: string; fileName: string }> = [];
    for (const r of group) {
      for (const doc of r.sourceDocuments) {
        if (!seenDocIds.has(doc.id)) {
          seenDocIds.add(doc.id);
          mergedDocs.push(doc);
        }
      }
    }

    result.push({
      ...primary,
      roles,
      sourceDocuments: mergedDocs,
      isCurrent: group.some((r) => r.isCurrent === true)
        ? true
        : group.some((r) => r.isCurrent === false)
          ? false
          : null,
    });
  }

  return result;
}

export function useEntityData(output: EntitiesDataTableProps["output"]) {
  // Build ACN→name lookup for resolving "Related To"
  const acnToName = useMemo(() => {
    const map = new Map<string, string>();
    for (const group of output.groups) {
      for (const entity of group.entities) {
        if (entity.acn) {
          map.set(entity.acn.replace(/\s+/g, ""), entity.name);
        }
      }
    }
    if (output.primaryEntity?.acn) {
      map.set(
        output.primaryEntity.acn.replace(/\s+/g, ""),
        output.primaryEntity.name,
      );
    }
    return map;
  }, [output]);

  // Flatten all groups into a single row array
  const rows = useMemo<EntityRow[]>(() => {
    const primaryName = output.primaryEntity?.name;
    const result: EntityRow[] = [];
    for (const group of output.groups) {
      // Resolve the parent entity name for this group
      const relatedTo: string | null = group.entityRef
        ? (acnToName.get(group.entityRef.replace(/\s+/g, "")) ??
          group.entityName ??
          null)
        : (group.entityName ?? primaryName ?? null);

      for (const entity of group.entities) {
        // Don't show "related to self"
        const isSelf = entity.name === relatedTo;
        const role: EntityRole = {
          group: group.label,
          groupCategory: group.category,
          groupEntityRef: group.entityRef ?? null,
          groupEntityName: group.entityName ?? null,
          detail: entity.detail,
        };
        result.push({
          name: entity.name,
          type: entity.type,
          group: group.label,
          groupCategory: group.category,
          groupEntityRef: group.entityRef ?? null,
          groupEntityName: group.entityName ?? null,
          detail: entity.detail,
          acn: entity.acn,
          abn: entity.abn,
          isCurrent: entity.isCurrent,
          isPrimary: entity.name === primaryName,
          relatedTo: isSelf ? null : relatedTo,
          sourceDocuments: entity.sourceDocuments ?? [],
          roles: [role],
        });
      }
    }
    return mergePersonRows(result);
  }, [output, acnToName]);

  // Build relationship index
  const relationshipIndex = useMemo(() => {
    const index = new Map<number, EntityRelationships>();
    const acnToRowIndices = new Map<string, number[]>();
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (r.acn) {
        const normalized = r.acn.replace(/\s+/g, "");
        const arr = acnToRowIndices.get(normalized) ?? [];
        arr.push(i);
        acnToRowIndices.set(normalized, arr);
      }
    }

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];

      const siblings = rows.filter(
        (r, ri) => ri !== i && r.group === row.group,
      );

      const associatedGroups: EntityRelationships["associatedGroups"] = [];
      if (row.acn) {
        const normalizedAcn = row.acn.replace(/\s+/g, "");
        const grouped = new Map<string, EntityRow[]>();
        for (const r of rows) {
          if (r === row) continue;
          if (
            r.groupEntityRef &&
            r.groupEntityRef.replace(/\s+/g, "") === normalizedAcn
          ) {
            const arr = grouped.get(r.groupCategory) ?? [];
            arr.push(r);
            grouped.set(r.groupCategory, arr);
          }
        }
        for (const [cat, entities] of grouped) {
          const meta = GROUP_CATEGORY_META[cat] ?? GROUP_CATEGORY_META.other;
          associatedGroups.push({
            label: meta.label,
            category: cat,
            icon: meta.icon,
            color: meta.color,
            entities,
          });
        }
      }

      const resolvedParentEntity: EntityRelationships["parentEntity"] = (() => {
        if (!row.groupEntityRef) return null;
        const normalizedRef = row.groupEntityRef.replace(/\s+/g, "");
        const parentIdx = acnToRowIndices.get(normalizedRef)?.[0];
        if (parentIdx !== undefined) {
          return { name: rows[parentIdx].name, acn: rows[parentIdx].acn };
        }
        if (row.groupEntityName) {
          return { name: row.groupEntityName, acn: row.groupEntityRef };
        }
        return null;
      })();
      const parentEntity: EntityRelationships["parentEntity"] =
        !resolvedParentEntity && !row.isPrimary && output.primaryEntity
          ? {
              name: output.primaryEntity.name,
              acn: output.primaryEntity.acn,
            }
          : resolvedParentEntity;

      index.set(i, { siblings, associatedGroups, parentEntity });
    }

    return index;
  }, [rows, output.primaryEntity]);

  // Unique types for the filter pills
  const entityTypes = useMemo(() => {
    const types = new Map<string, number>();
    for (const row of rows) {
      types.set(row.type, (types.get(row.type) ?? 0) + 1);
    }
    return Array.from(types.entries()).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  return { rows, relationshipIndex, entityTypes };
}
